"use client";

import {
  AnimateHeightBox,
  Box,
  ButtCon,
  Heading,
  Icon,
  Stack,
} from "@biom3/react";
import type { Content } from "@prismicio/client";
import type { WishlistProps } from "@ui-kit";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";

type QuestDrawerProps = {
  wishlistProps: WishlistProps;
  isExpanded?: boolean;
  onExpandedChange?: (state: boolean) => void;
  game: Content.GameDocument;
  useMobileLayoutVariant: boolean;
  children: React.ReactNode;
  visible?: boolean;
  twitchDropsGameUid?: string;
};

export const QuestDrawer = ({
  wishlistProps,
  isExpanded = true,
  onExpandedChange,
  game,
  useMobileLayoutVariant,
  visible = true,
  children,
  twitchDropsGameUid,
}: QuestDrawerProps) => {
  const [internalExpanded, setInternalExpanded] = useState(true);
  const { isWishlisted } = wishlistProps;
  const router = useRouter();

  const expanded = isExpanded ?? internalExpanded;
  const drawerWidth = useMobileLayoutVariant ? "100%" : "480px";

  const handleExpandClick = () => {
    const newState = !expanded;
    if (onExpandedChange) {
      onExpandedChange(newState);
    } else {
      setInternalExpanded(newState);
    }
  };

  const handleTwitchDropsClick = useCallback(() => {
    router.push(`/twitch-drops/${twitchDropsGameUid}`);
  }, [router, twitchDropsGameUid]);

  return (
    <AnimateHeightBox
      sx={{
        position: "fixed",
        bottom: useMobileLayoutVariant ? "60px" : "0px",
        display: visible ? "block" : "none",
        right: useMobileLayoutVariant ? "50%" : "0",
        transform: useMobileLayoutVariant ? "translateX(50%)" : "none",
        width: drawerWidth,
        backgroundColor: "base.color.translucent.inverse.700",
        backdropFilter: "blur(16px)",
        borderTopLeftRadius: "base.borderRadius.x10",
        borderTopRightRadius: "base.borderRadius.x10",
        border: "1px solid",
        borderColor: "base.color.translucent.standard.500",
        borderBottom: "none",
        zIndex: "4",
        mr: useMobileLayoutVariant ? "0" : "base.spacing.x8",
        maxHeight: "90vh",
        pt: expanded && useMobileLayoutVariant ? "60px" : "0px",
      }}
      motionProfile="gentle"
    >
      <Stack
        direction="column"
        sx={{
          width: "100%",
          height: "auto",
          maxw: "770px",
        }}
      >
        <Box
          onClick={handleExpandClick}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "base.spacing.x6",
            cursor: "pointer",
            position: "relative",
            width: "100%",
            borderTopLeftRadius: "base.borderRadius.x10",
            borderTopRightRadius: "base.borderRadius.x10",
            pb:
              useMobileLayoutVariant && expanded && !isWishlisted
                ? "0px"
                : "base.spacing.x6",
          }}
        >
          <ButtCon
            icon={expanded ? "ChevronExpand" : "ChevronCollapse"}
            iconVariant="bold"
            variant="tertiary"
            size="medium"
            sx={{
              position: "absolute",
              left: "base.spacing.x4",
            }}
          />
          <Heading
            size="small"
            sx={{
              maxWidth: useMobileLayoutVariant ? "calc(100% - 120px)" : "340px",
              textAlign: "center",
            }}
          >
            {isWishlisted
              ? (game.data.following_quest_header ??
                `Keep supporting ${game.data.name}`)
              : (game.data.not_following_quest_header ??
                `Get behind ${game.data.name}`)}
          </Heading>
          {twitchDropsGameUid && (
            <Box sx={{ position: "absolute", right: "base.spacing.x4" }}>
              <Icon
                icon="Twitch"
                sx={{
                  w: "base.icon.size.400",
                  fill: "#a855f7",
                  animation: "pulse 2s infinite",
                  "@keyframes pulse": {
                    "0%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.2)" },
                    "100%": { transform: "scale(1)" },
                  },
                }}
                onClick={handleTwitchDropsClick}
              />
            </Box>
          )}
        </Box>

        {expanded && children}
      </Stack>
    </AnimateHeightBox>
  );
};

export const QuestDrawerContainer = dynamic(
  () => Promise.resolve(QuestDrawer),
  { ssr: false },
);
